import { useState, useEffect } from "react";
import React from "react";
import clsx from "classnames";
import AuthContext from "../AuthContext";
import SignedOutHeader from "../SignedOutHeader/SignedOutHeader";
import Footer from "../Footer/Footer";
import { usePostHog } from "posthog-js/react";
import { Link, useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Loader2, Palette } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import TrialDialog from "../../components/TrialDialog/TrialDialog";
import { PURCHASES_CLOSED } from "../../shutdown";

export default function PricingPage2() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { t } = useTranslation();
  // pass useState() the initial value

  // machineType can be "ondemand", "100gb", "200gb", "1tb"
  const [paymentPeriod, setPaymentPeriod] = useState("week");
  const [paymentType, setPaymentType] = useState("subscription");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState(null);
  const [trialDialogIsOpen, setTrialDialogIsOpen] = useState(false);

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

  const planPriceIds =
    process.env.NODE_ENV === "development"
      ? {
        n00b: {
          week: "price_1QqXytSAcn2IgTItnpXjH9vL",
          month: "price_1QqXzeSAcn2IgTItL0Up5pe0",
        },
        Casual: {
          week: "price_1QqY1YSAcn2IgTIt5kgdvCoZ",
          month: "price_1Qom49SAcn2IgTItgNbT1pxP",
        },
        Pro: {
          week: "price_1QqY2wSAcn2IgTItytwKO4Ag",
          month: "price_1QqY3fSAcn2IgTItQDeynod4",
        },
        Starter: {
          week: "price_1RHPRHSAcn2IgTIt2LuxyU8D",
          month: "", // No such plan exists
        }
      }
      : {
        n00b: {
          week: "price_1QqEJhSAcn2IgTItyDNCDRE7",
          month: "price_1QqEKUSAcn2IgTItO6OMsi5F",
        },
        Casual: {
          week: "price_1QqELYSAcn2IgTItBGlxkDfD",
          month: "price_1QqEMeSAcn2IgTItooL6YJG2",
        },
        Pro: {
          week: "price_1QqDb2SAcn2IgTItS6dcW2Ij",
          month: "price_1QqDfRSAcn2IgTIt6upcgDM6",
        },
        Starter: {
          week: "price_1RHPUgSAcn2IgTIt9NDQEhiZ",
          month: "", // no such plan exists
        }
      };
  const {
    user,
    signOut,
    signIn,
    userEmail,
    fetchUserPlan,
    userPlan,
    country,
    paymentGateway,
    determineCountry,
  } = React.useContext(AuthContext);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchUserPlan(user?.signInUserSession?.idToken);
    determineCountry();
    loadScript(RAZORPAY_SCRIPT_URL).then((loaded) => {
      if (!loaded) {
        console.log(
          "Failed to load Razorpay SDK. Please check your internet connection and try again."
        );
      }
    });
  }, []);

  const renderBuyButton = function (planName) {
    if (PURCHASES_CLOSED) {
      return (
        <Button asChild className="w-full rounded-full" variant="outline">
          <Link to="/shutdown">{t("purchases_closed")}</Link>
        </Button>
      );
    }
    if (userPlan && userPlan.length > 0 && userPlan[0].purchases) {
      if (
        userPlan[0].plan_name?.startsWith(planName) &&
        userPlan[0].purchases[0].subscription === paymentPeriod
      ) {
        return (
          <Button className="w-full rounded-full" disabled>
            <Check className="w-5 h-5 overflow-hidden"></Check>
            {planName} Selected
          </Button>
        );
      } else {
        return (
          <Button
            className="w-full rounded-full"
            onClick={() =>
              handleBuyClick(
                planName,
                paymentPeriod,
                "subscription",
                1,
                paymentGateway,
                false
              )
            }
          >
            Select {planName}
          </Button>
        );
      }
    }
    return (
      <Button
        className="w-full rounded-full"
        onClick={() =>
          handleBuyClick(
            planName,
            paymentPeriod,
            "subscription",
            1,
            paymentGateway,
            false
          )
        }
      >
        Get Started
      </Button>
    );
  };

  const toggleTrialDialog = function () {
    if (PURCHASES_CLOSED) {
      navigate("/shutdown");
      return;
    }
    setTrialDialogIsOpen(true);
  };

  async function handleBuyClick(
    plan,
    paymentPeriod,
    payment_mode,
    quantity,
    payment_gateway,
    isHourlyPlan
  ) {
    if (PURCHASES_CLOSED) {
      navigate("/shutdown");
      return;
    }
    if (!user) {
      navigate("/signup");
    }
    const priceId = planPriceIds[plan][paymentPeriod];
    posthog?.capture("purchase_plan", {
      priceId,
      payment_mode,
      quantity,
      paymentPeriod,
      paymentType,
      paymentPeriod,
      paymentType,
      userEmail,
    });
    setCheckoutLoading(true);
    let products = [
      {
        priceId: priceId,
        isHourlyPlan: isHourlyPlan,
        adjustableQuantity: false,
        quantity: quantity,
        email: checkoutEmail,
        vmMachineType: null,
        vmPaymentPeriod: null,
        vmPaymentType: null,
        hoursPaymentPeriod: null,
        hoursPaymentType: null,
        planName: plan,
        paymentPeriod: paymentPeriod,
      },
    ];
    await fetch(
      payment_gateway === "stripe"
        ? process.env.NODE_ENV === "development"
          ? "https://1ku3nifl2e.execute-api.ap-south-1.amazonaws.com/default/getStripeCheckout-dev-hello"
          : "https://v1wapno6ch.execute-api.ap-south-1.amazonaws.com/prod/getStripeCheckout-prod-hello"
        : process.env.NODE_ENV === "development"
          ? "https://9mljq6r4e3.execute-api.ap-south-1.amazonaws.com/default/getRazorpayCheckout-dev-hello"
          : "https://d58cycm35h.execute-api.ap-south-1.amazonaws.com/default/getRazorpayCheckout-prod-hello",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: user.signInUserSession.idToken.jwtToken,
        },
        body: JSON.stringify({
          payment_mode: payment_mode,
          products: products,
          currency: "usd",
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setTrialDialogIsOpen(false);
        if (data.checkout_url) {
          fetchUserPlan(user.signInUserSession.idToken, userEmail);
          window.location.href = data.checkout_url;
        } else if (data.order_id && payment_gateway === "razorpay") {
          const options = {
            key: data.key_id,
            amount: data.amount, // Amount should be in currency subunits
            currency: "INR",
            name: "GameAway",
            order_id: data.order_id,
            handler: function (response) {
              fetchUserPlan(user.signInUserSession.idToken, userEmail);
              navigate("/confirmation");
            },
            prefill: {
              email: checkoutEmail,
            },
            theme: {
              color: "#231955",
            },
          };
          const paymentObject = new window.Razorpay(options);
          debugger;
          paymentObject.open();
        } else if (data.subscription_id && payment_gateway === "razorpay") {
          const options = {
            key: data.key_id, // Enter the Key ID generated from the Razorpay Dashboard
            subscription_id: data.subscription_id,
            name: "GameAway",
            handler: function (response) {
              fetchUserPlan(user.signInUserSession.idToken, userEmail);
              navigate("/confirmation");
            },
            prefill: {
              email: checkoutEmail,
            },
            theme: {
              color: "#231955",
            },
          };
          const paymentObject = new window.Razorpay(options);
          paymentObject.open();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setCheckoutLoading(false);
      });
  }

  return (
    <div className="pt-0 max-w-6xl px-6 my-0 mx-auto min-h-screen">
      <SignedOutHeader />
      <input
        className={clsx("w-36 mx-auto", {
          hidden: user?.username !== "utkarshdalal",
        })}
        placeholder="customer email"
        onChange={(ev) => setCheckoutEmail(ev.target.value)}
      />
      <section className="py-6">
        <div className="flex flex-col items-center gap-6 ">
          <div className="w-full mx-auto flex md:flex-row flex-col justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-foreground">Plans</h1>
              <p className="text-base text-muted-foreground">
                Pick your plan below
              </p>
            </div>

            <div className="flex justify-start items-center gap-3 mt-4">
              <Button
                variant="secondary"
                asChild
                className="rounded-full w-fit cursor-pointer"
              >
                <Link to="/custom-pricing">{t("need_custom_plan")}</Link>
              </Button>
              <TrialDialog
                user={user}
                paymentGateway={paymentGateway}
                buyOneHour={true}
                handleBuyClickFn={handleBuyClick}
                checkoutLoading={checkoutLoading}
                isOpen={trialDialogIsOpen}
                toggleTrialDialog={toggleTrialDialog}
              />
            </div>
          </div>
          <Separator className="w-full" />

          <div className="w-full md:w-fit">
            <Tabs
              defaultValue={paymentPeriod}
              onValueChange={setPaymentPeriod}
              className="w-full md:w-fit"
            >
              <TabsList className="grid w-full grid-cols-2 h-fit rounded-xl border border-[#161b26] p-1 bg-[var(--card)] bg-opacity-0">
                <TabsTrigger
                  value="week"
                  className="px-3 py-1.5 rounded-lg data-[state=active]:bg-[var(--button-background)]"
                >
                  Weekly
                </TabsTrigger>
                <TabsTrigger
                  value="month"
                  className="px-3 py-1.5 rounded-lg gap-2 flex data-[state=active]:bg-[var(--button-background)] lg:justify-between"
                >
                  <div className="text-center text-sm font-medium leading-tight">
                    Monthly
                  </div>
                  <div className="px-2.5 py-0.5 rounded-full border border-[var(--border-tertiary)] justify-center items-center gap-2.5 flex">
                    <div className="text-xs font-semibold leading-none">
                      -5%
                    </div>
                  </div>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
            {/* Noob Plan */}
            <Card className="rounded-2xl border-secondary">
              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <h3 className="text-2xl font-semibold">n00b</h3>
                  <p className="text-sm text-muted-foreground">
                    Perfect for getting started
                  </p>
                </div>
                <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                {!paymentGateway ? (
                  <div>
                    <div className="flex items-baseline text-foreground">
                      <Skeleton className="h-10 w-2/3" />
                    </div>
                  </div>
                ) : paymentGateway === "stripe" ? (
                  <div className="flex items-baseline text-foreground">
                    <span className="text-2xl font-semibold">$</span>
                    <span className="text-4xl font-bold">
                      {paymentPeriod === "week" ? 3.49 : 13.99}
                    </span>
                    <span className="ml-1 text-sm text-muted-foreground">
                      / {paymentPeriod}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline text-foreground">
                    <span className="text-2xl font-semibold">₹</span>
                    <span className="text-4xl font-bold">
                      {paymentPeriod === "week" ? 250 : 250 * 4}
                    </span>
                    <span className="ml-1 text-sm text-muted-foreground">
                      / {paymentPeriod} + 18% GST
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-muted-foreground">
                    {paymentPeriod === "week" ? 5 : 21.25} hours playtime{" "}
                    {paymentPeriod}ly
                  </p>

                  <Popover>
                    <PopoverTrigger asChild>
                      <p className="text-neutral-50 text-base font-normal decoration-dotted underline cursor-help">
                        Private PC – 100GB
                      </p>
                    </PopoverTrigger>
                    <PopoverContent className="border-secondary">
                      <p>{t("vm_tooltip")}</p>
                    </PopoverContent>
                  </Popover>
                </div>
                {checkoutLoading ? (
                  <Button disabled className="w-full rounded-full">
                    <Loader2 className="animate-spin" />
                    Loading
                  </Button>
                ) : (
                  renderBuyButton("n00b")
                )}
                <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                <div className="flex gap-4">
                  <Check className="text-neutral-50 min-w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                  <Popover>
                    <PopoverTrigger asChild>
                      <span className="text-muted-foreground decoration-dotted underline cursor-help">
                        {t("install_your_games")}
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="border-secondary">
                      <p>{t("vm_tooltip")}</p>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex gap-4">
                  <Check className="text-neutral-50 min-w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                  <span className="text-muted-foreground w-full">
                    {!paymentGateway ? (
                      <Skeleton className="h-6 w-2/3" />
                    ) : paymentGateway === "stripe" ? (
                      paymentPeriod === "month" ? (
                        <span>100GB storage space ($10 value)</span>
                      ) : (
                        <span>100GB storage space ($2.50 value)</span>
                      )
                    ) : paymentPeriod === "month" ? (
                      <span>100GB storage space (₹550 value)</span>
                    ) : (
                      <span>100GB storage space (₹150 value)</span>
                    )}
                  </span>
                </div>

                {[
                  t("cloud_local_saves"),
                  t("virtual_gaming_pc_servers"),
                  t("shareable_with_friends"),
                  t("add_playtime_anytime"),
                ].map((feature) => (
                  <div key={feature} className="flex gap-4">
                    <Check className="text-neutral-50 min-w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Contender Plan */}
            <Card className="rounded-2xl border-secondary">
              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 relative">
                    <h3 className="text-2xl font-semibold">Casual</h3>
                    <Badge
                      variant="secondary"
                      className="absolute bg-fuchsia-900 text-fuchsia-200 right-0 top-0"
                    >
                      Popular
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Most popular for regular gamers
                  </p>
                </div>
                <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                {!paymentGateway ? (
                  <div>
                    <div className="flex items-baseline text-foreground">
                      <Skeleton className="h-10 w-2/3" />
                    </div>
                  </div>
                ) : paymentGateway === "stripe" ? (
                  <div className="flex items-baseline text-foreground">
                    <span className="text-2xl font-semibold">$</span>
                    <span className="text-4xl font-bold">
                      {paymentPeriod === "week" ? 7.99 : 31.99}
                    </span>
                    <span className="ml-1 text-sm text-muted-foreground">
                      / {paymentPeriod}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline text-foreground">
                    <span className="text-2xl font-semibold">₹</span>
                    <span className="text-4xl font-bold">
                      {paymentPeriod === "week" ? 500 : 500 * 4}
                    </span>
                    <span className="ml-1 text-sm text-muted-foreground">
                      / {paymentPeriod} + 18% GST
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-muted-foreground">
                    {paymentPeriod === "week" ? 15 : 63.75} hours playtime{" "}
                    {paymentPeriod}ly
                  </p>

                  <Popover>
                    <PopoverTrigger asChild>
                      <p className="text-neutral-50 text-base font-normal decoration-dotted underline cursor-help">
                        Private PC – 150GB
                      </p>
                    </PopoverTrigger>
                    <PopoverContent className="border-secondary">
                      <p>{t("vm_tooltip")}</p>
                    </PopoverContent>
                  </Popover>
                </div>
                {checkoutLoading ? (
                  <Button disabled className="w-full rounded-full">
                    <Loader2 className="animate-spin" />
                    Loading
                  </Button>
                ) : (
                  renderBuyButton("Casual")
                )}
                <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                <div key={t("install_your_games")} className="flex gap-4">
                  <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                  <Popover>
                    <PopoverTrigger asChild>
                      <span className="text-muted-foreground decoration-dotted underline cursor-help">
                        {t("install_your_games")}
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="border-secondary">
                      <p>{t("vm_tooltip")}</p>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex gap-4">
                  <Check className="text-neutral-50 min-w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                  <span className="text-muted-foreground w-full">
                    {!paymentGateway ? (
                      <Skeleton className="h-6 w-2/3" />
                    ) : paymentGateway === "stripe" ? (
                      paymentPeriod === "month" ? (
                        <span>150GB storage space ($14 value)</span>
                      ) : (
                        <span>150GB storage space ($3.75 value)</span>
                      )
                    ) : paymentPeriod === "month" ? (
                      <span>150GB storage space (₹825 value)</span>
                    ) : (
                      <span>150GB storage space (₹225 value)</span>
                    )}
                  </span>
                </div>
                {[
                  t("cloud_local_saves"),
                  t("virtual_gaming_pc_servers"),
                  t("shareable_with_friends"),
                  t("add_playtime_anytime"),
                ].map((feature) => (
                  <div key={feature} className="flex gap-4">
                    <Check className="text-neutral-50 min-w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Champion Plan */}
            <Card className="rounded-2xl border-secondary">
              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <h3 className="text-2xl font-semibold">Pro</h3>
                  <p className="text-sm text-muted-foreground">
                    Best value for serious gamers
                  </p>
                </div>
                <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                {!paymentGateway ? (
                  <div>
                    <div className="flex items-baseline text-foreground">
                      <Skeleton className="h-10 w-2/3" />
                    </div>
                  </div>
                ) : paymentGateway === "stripe" ? (
                  <div className="flex items-baseline text-foreground">
                    <span className="text-2xl font-semibold">$</span>
                    <span className="text-4xl font-bold">
                      {paymentPeriod === "week" ? 12.99 : 51.99}
                    </span>
                    <span className="ml-1 text-sm text-muted-foreground">
                      / {paymentPeriod}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline text-foreground">
                    <span className="text-2xl font-semibold">₹</span>
                    <span className="text-4xl font-bold">
                      {paymentPeriod === "week" ? 975 : 975 * 4}
                    </span>
                    <span className="ml-1 text-sm text-muted-foreground">
                      / {paymentPeriod} + 18% GST
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-muted-foreground">
                    {paymentPeriod === "week" ? 30 : 127.5} hours playtime{" "}
                    {paymentPeriod}ly
                  </p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <p className="text-neutral-50 text-base font-normal decoration-dotted underline cursor-help">
                        Private PC – 200GB
                      </p>
                    </PopoverTrigger>
                    <PopoverContent className="border-secondary">
                      <p>{t("vm_tooltip")}</p>
                    </PopoverContent>
                  </Popover>
                </div>
                {checkoutLoading ? (
                  <Button disabled className="w-full rounded-full">
                    <Loader2 className="animate-spin" />
                    Loading
                  </Button>
                ) : (
                  renderBuyButton("Pro")
                )}
                <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                <div key={t("install_your_games")} className="flex gap-4">
                  <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                  <Popover>
                    <PopoverTrigger asChild>
                      <span className="text-muted-foreground decoration-dotted underline cursor-help">
                        {t("install_your_games")}
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="border-secondary">
                      <p>{t("vm_tooltip")}</p>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex gap-4">
                  <Check className="text-neutral-50 min-w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                  <span className="text-muted-foreground w-full">
                    {!paymentGateway ? (
                      <Skeleton className="h-6 w-2/3" />
                    ) : paymentGateway === "stripe" ? (
                      paymentPeriod === "month" ? (
                        <span>200GB storage space ($20 value)</span>
                      ) : (
                        <span>200GB storage space ($5 value)</span>
                      )
                    ) : paymentPeriod === "month" ? (
                      <span>200GB storage space (₹1100 value)</span>
                    ) : (
                      <span>200GB storage space (₹300 value)</span>
                    )}
                  </span>
                </div>
                {[
                  t("cloud_local_saves"),
                  t("virtual_gaming_pc_servers"),
                  t("shareable_with_friends"),
                  t("add_playtime_anytime"),
                ].map((feature) => (
                  <div key={feature} className="flex gap-4">
                    <Check className="text-neutral-50 min-w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
